import { Fragment, useMemo, useState, useEffect } from "react";
import { Button } from "~/atoms/button";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/atoms/table";
import { cn } from "~/lib/utils";
import NoResult from "./NoResult";
import { ArrowDown, ChevronRight, Pin } from "lucide-react";

type CompareFn<T> = (a: T, b: T) => number;

export interface TableColumn<T> {
  columnKey?: string;
  header?: React.ReactNode;
  defaultSort?: boolean;
  compareFn?: CompareFn<T>;
  cell: (item: T) => React.ReactNode;
  cellClassName?: string;
  tableHeadClassName?: string;
}

interface Props<T> {
  columns: TableColumn<T>[];
  data: T[];
  onClickRow?: (item: T) => void;
  renderExpandedRow?: (item: T) => React.ReactNode;
  enablePinning?: boolean;
  expandAll?: boolean;
  storageKey?: string;
  wrapperClassName?: string;
}

interface SortState<T> {
  columnIndex: number | undefined;
  order: "asc" | "desc";
  compareFn: CompareFn<T> | undefined;
}

const SimpleDataTable = <T extends { id: number; isPinned?: boolean }>({
  columns,
  data,
  onClickRow,
  renderExpandedRow,
  enablePinning = false,
  expandAll = false,
  storageKey,
  wrapperClassName,
}: Props<T>) => {
  const initState = getInitState(columns);
  const [sortState, setSortState] = useState<SortState<T> | undefined>(
    initState,
  );
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const [pinnedRows, setPinnedRows] = useState<Set<number>>(() => {
    if (!enablePinning || !storageKey) return new Set();
    try {
      const stored = localStorage.getItem(`pinned_rows_${storageKey}`);
      return stored ? new Set(JSON.parse(stored) as number[]) : new Set();
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    if (enablePinning && storageKey) {
      localStorage.setItem(
        `pinned_rows_${storageKey}`,
        JSON.stringify(Array.from(pinnedRows)),
      );
    }
  }, [pinnedRows, enablePinning, storageKey]);

  const onClickHead = (columnIndex: number, compareFn: CompareFn<T>) => {
    const newOrder =
      columnIndex === sortState?.columnIndex && sortState?.order === "asc"
        ? "desc"
        : "asc";

    setSortState({
      columnIndex,
      order: newOrder,
      compareFn,
    });
  };

  const toggleRowExpansion = (index: number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const togglePinRow = (id: number) => {
    setPinnedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const sortedData = useMemo(() => {
    const pinnedData = data.filter((item) => pinnedRows.has(item.id));
    const unpinnedData = data.filter((item) => !pinnedRows.has(item.id));

    const sortedUnpinnedData = sortState
      ? unpinnedData
          .slice()
          .sort(
            (a, b) =>
              sortState.compareFn!(a, b) * (sortState.order === "asc" ? 1 : -1),
          )
      : unpinnedData;

    return enablePinning
      ? [...pinnedData, ...sortedUnpinnedData]
      : sortedUnpinnedData;
  }, [data, sortState, pinnedRows, enablePinning]);

  const nbrOfColumns =
    columns.length + (enablePinning ? 1 : 0) + (renderExpandedRow ? 1 : 0);

  return (
    <Table wrapperClassName={wrapperClassName}>
      <TableHeader className="bg-card sticky top-0 z-10">
        <TableRow>
          {renderExpandedRow && <TableHead className="w-14" />}
          {columns.map(({ header, tableHeadClassName, compareFn }, i) => (
            <TableHead key={i} className={tableHeadClassName}>
              {header ? (
                compareFn ? (
                  <Button
                    variant="ghost"
                    onClick={() => onClickHead(i, compareFn)}
                    className="gap-1 p-0 hover:bg-transparent hover:underline"
                  >
                    {header}
                    <ArrowDown
                      className={cn({
                        "rotate-180": sortState?.order === "desc",
                        hidden: sortState?.columnIndex !== i,
                      })}
                    />
                  </Button>
                ) : (
                  header
                )
              ) : null}
            </TableHead>
          ))}
          {enablePinning && <TableHead className="w-14" />}
        </TableRow>
      </TableHeader>
      {sortedData.length === 0 ? (
        <TableBody>
          <TableRow>
            <TableCell colSpan={nbrOfColumns}>
              <NoResult />
            </TableCell>
          </TableRow>
        </TableBody>
      ) : (
        <TableBody>
          {sortedData.map((item, i) => {
            const isExpanded = expandAll || expandedRows.has(i);

            return (
              <Fragment key={`${i}`}>
                <TableRow
                  className={cn({
                    "cursor-pointer": onClickRow,
                    "border-transparent": isExpanded,
                  })}
                  onClick={() => onClickRow?.(item)}
                >
                  {renderExpandedRow && (
                    <TableCell
                      onClick={
                        expandAll ? undefined : () => toggleRowExpansion(i)
                      }
                      className="cursor-pointer"
                    >
                      <ChevronRight
                        className={cn(
                          "size-5 transition-transform duration-300",
                          {
                            "rotate-90": isExpanded,
                          },
                        )}
                      />
                    </TableCell>
                  )}
                  {columns.map((column, j) => (
                    <TableCell key={j} className={column.cellClassName}>
                      {column.cell(item)}
                    </TableCell>
                  ))}
                  {enablePinning && (
                    <TableCell
                      onClick={() => togglePinRow(item.id)}
                      className="cursor-pointer"
                    >
                      <Pin
                        fill={pinnedRows.has(item.id) ? "currentColor" : "none"}
                        className={cn("size-5", {
                          "text-info": pinnedRows.has(item.id),
                        })}
                      />
                    </TableCell>
                  )}
                </TableRow>
                {isExpanded && renderExpandedRow && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell
                      colSpan={nbrOfColumns}
                      // Need to hardcode the width due to table layout issues
                      className="max-w-[calc(100vw-5rem)] overflow-x-auto 2xl:max-w-(--breakpoint-2xl)"
                    >
                      {renderExpandedRow(item)}
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      )}
    </Table>
  );
};

export default SimpleDataTable;

const getInitState = <T,>(c: TableColumn<T>[]): SortState<T> | undefined => {
  const defaultIndex = c.findIndex((col) => col.defaultSort);

  return defaultIndex !== -1
    ? {
        columnIndex: defaultIndex,
        order: "asc",
        compareFn: c[defaultIndex]?.compareFn,
      }
    : undefined;
};
